<script setup>
import Dialog from 'primevue/dialog';
import { ref, computed } from 'vue';
import { useUserStore } from '@/store/user-store';
import AnButton from '../common/AnButton.vue';

const props = defineProps({ modelValue: Boolean });
const emit = defineEmits(['update:modelValue']);
const store = useUserStore();

const incomeHeads = ['Earned Income', 'Passive Income', 'Portfolio Income'];
const incomeTypes = ['Salary', 'Self Employed', 'Rental Income', 'Side Hustle/Business', 'Interest', 'Dividends'];
const selectedHead = ref(incomeHeads[0]);
const selectedType = ref(incomeTypes[0]);
const amount = ref('');
const date = ref('');
const error = ref('');

const closeDialog = () => emit('update:modelValue', false);

const handleAdd = async () => {
  // Find itemId if exists
  const existing = (store.financialStatement?.customerIncome || []).find(
    i => i.incomeTyp === selectedType.value
  );
  const payload = {
    records: [{
      itemId: existing ? existing.itemId : null,
      incomeHead: selectedHead.value,
      incomeType: selectedType.value,
      incomeDate: date.value,
      incomeAmount: amount.value
    }]
  };
  try {
    await store.updateFinancialStatement(payload);
    closeDialog();
  } catch (e) {
    error.value = 'Failed to update income.';
  }
};
</script>
<template>
  <Dialog modal :visible="modelValue" :closable="false" :dismissable-mask="false" @update:visible="closeDialog">
    <i @click="closeDialog" class="pi pi-times close-btn" />
    <template #header>
      <div class="dialog-header">
        <span class="dialog-title">Manage Income</span>
      </div>
    </template>
    <div class="dialog-content">
      <div class="dialog-section">
        <div class="dialog-row-flex">
          <div class="dialog-col">
            <label class="dialog-label">Income Head</label>
            <select v-model="selectedHead" class="dialog-input">
              <option v-for="head in incomeHeads" :key="head" :value="head">{{ head }}</option>
            </select>
          </div>
          <div class="dialog-col">
            <label class="dialog-label">Income Type</label>
            <select v-model="selectedType" class="dialog-input">
              <option v-for="type in incomeTypes" :key="type" :value="type">{{ type }}</option>
            </select>
          </div>
        </div>
        <div class="dialog-row-flex">
          <div class="dialog-col">
            <label class="dialog-label">Amount</label>
            <input v-model="amount" class="dialog-input" type="number" placeholder="Amount" />
          </div>
          <div class="dialog-col">
            <label class="dialog-label">Date</label>
            <input v-model="date" class="dialog-input" type="date" />
          </div>
        </div>
        <div class="dialog-footer">
          <AnButton label="Back" variant="tab" @click="closeDialog" />
          <AnButton label="Add" variant="primary" @click="handleAdd" />
        </div>
      </div>
      <div v-if="error" class="error-msg">{{ error }}</div>
    </div>
  </Dialog>
</template>
<style scoped>
:deep(.p-dialog) {
  width: 400px !important;
  max-width: 400px !important;
  min-width: 320px !important;
}
.dialog-header {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 16px 0 0 0;
  min-height: 40px;
  border-bottom: 1px solid #e2e8f0;
}
.dialog-title {
  color: var(--an-primary);
  font-size: 20px;
  font-weight: 700;
  flex: 1;
  text-align: center;
  margin: 0 auto;
  padding-left: 12px;
}
.close-btn {
  position: absolute;
  right: 10px;
  top: 10px;
  background: var(--an-error);
  color: #fff;
  padding: 6px 8px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 20px;
  z-index: 10;
}
.dialog-content {
  padding: 18px 18px 0 18px;
  min-height: 340px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: 100%;
  box-sizing: border-box;
}
.dialog-section {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px 12px 24px 12px;
  margin-bottom: 10px;
  width: 100%;
  box-sizing: border-box;
}
.dialog-row-flex {
  display: flex;
  flex-direction: row;
  gap: 24px;
  margin-bottom: 36px;
}
.dialog-col {
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  min-width: 0;
  width: 100%;
}
.dialog-label {
  font-weight: 600;
  margin-bottom: 2px;
  font-size: 15px;
}
.dialog-input {
  padding: 12px;
  border: 1.5px solid var(--an-gray1);
  border-radius: 5px;
  font-size: 15px;
  width: 100%;
  box-sizing: border-box;
}
.dialog-input:focus {
  outline: none;
  border-color: var(--an-primary);
}
.dialog-footer {
  display: flex;
  justify-content: center;
  gap: 14px;
  width: 100%;
  padding: 10px 0 0 0;
  margin-bottom: 10px;
}
.error-msg {
  color: #c92a2a;
  font-size: 1rem;
  margin: 0 12px 8px 12px;
}
@media (max-width: 500px) {
  :deep(.p-dialog) {
    width: 98vw !important;
    min-width: 0 !important;
    max-width: 100vw !important;
  }
  .dialog-row-flex {
    flex-direction: column !important;
    gap: 6px !important;
  }
}
</style>
